import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Plus, Trash2, Download, Printer, Mail, Image as ImageIcon, Save, CheckCircle2 } from "lucide-react";
import {
  defaultInvoice,
  getBillingSettings,
  getInvoice,
  createInvoice,
  updateInvoice,
  sendInvoice,
  markInvoicePaid,
  downloadInvoicePdf,
  computeTotals,
  CURRENCIES,
} from "../../services/invoiceService";
import { getProject } from "../../services/projectService";
import { useAuthedImage } from "../../hooks/useAuthedImage";

const field = { fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", width: "100%" };
const label = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 5 };

const Card = ({ title, children, extra }) => (
  <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05)", overflow: "hidden", marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{title}</h6>
      {extra}
    </div>
    <div style={{ padding: 18 }}>{children}</div>
  </div>
);

const InvoiceEditor = () => {
  const { id: projectPid, invoiceId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [invoice, setInvoice] = useState(() => defaultInvoice(projectPid));
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [showEmailBox, setShowEmailBox] = useState(false);
  const logoSrc = useAuthedImage(invoice.theme?.logoUrl);

  // New invoice: pre-fill from the project's billing settings template.
  useEffect(() => {
    if (!projectPid || invoiceId) return;
    getBillingSettings(projectPid)
      .then((res) => {
        const s = res.data?.response;
        if (!s) return;
        setInvoice((inv) => ({
          ...inv,
          documentTitle: s.documentTitle ?? inv.documentTitle,
          currency: s.currency ?? inv.currency,
          currencySymbol: s.currencySymbol ?? inv.currencySymbol,
          theme: s.theme ?? inv.theme,
          from: s.from ?? inv.from,
          to: s.to ?? inv.to,
          items: s.fixedCharges?.length ? s.fixedCharges : inv.items,
          taxes: s.taxes ?? inv.taxes,
          discounts: s.discounts ?? inv.discounts,
          customFields: s.customFields ?? inv.customFields,
        }));
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to load billing settings"));
  }, [projectPid, invoiceId]);

  // Fallback: if billing settings have no business name yet, use the project name.
  useEffect(() => {
    if (!projectPid || invoiceId) return;
    getProject(projectPid)
      .then((res) => {
        const p = res.data?.response ?? res.data;
        if (p) setInvoice((inv) => (inv.from.name ? inv : { ...inv, from: { ...inv.from, name: p.project_name || inv.from.name } }));
      })
      .catch(() => {});
  }, [projectPid, invoiceId]);

  useEffect(() => {
    if (!invoiceId) return;
    getInvoice(invoiceId)
      .then((res) => setInvoice(res.data.response))
      .catch(() => navigate(`/project-details/${projectPid}/invoices`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const update = (patch) => setInvoice((inv) => ({ ...inv, ...patch }));
  const updateSection = (section, patch) => setInvoice((inv) => ({ ...inv, [section]: { ...inv[section], ...patch } }));

  const updateItem = (idx, patch) =>
    setInvoice((inv) => ({
      ...inv,
      items: inv.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));

  const addItem = () => setInvoice((inv) => ({ ...inv, items: [...inv.items, { description: "", quantity: 1, rate: 0 }] }));
  const removeItem = (idx) => setInvoice((inv) => ({ ...inv, items: inv.items.filter((_, i) => i !== idx) }));

  const updateRow = (section, idx, patch) =>
    setInvoice((inv) => ({
      ...inv,
      [section]: inv[section].map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  const addRow = (section, row) => setInvoice((inv) => ({ ...inv, [section]: [...inv[section], row] }));
  const removeRow = (section, idx) => setInvoice((inv) => ({ ...inv, [section]: inv[section].filter((_, i) => i !== idx) }));

  const onCurrencyChange = (code) => {
    const c = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];
    update({ currency: c.code, currencySymbol: c.symbol });
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => updateSection("theme", { logoUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const handleSave = async (status) => {
    setSaving(true);
    const targetStatus = status || invoice.status;
    try {
      if (!invoice.invoicePid) {
        const payload = {
          projectPid,
          issueDate: invoice.issueDate,
          status: targetStatus,
          items: invoice.items,
        };
        if (invoice.invoiceNumber) payload.invoiceNumber = invoice.invoiceNumber;
        if (invoice.dueDate) payload.dueDate = invoice.dueDate;
        if (invoice.poNumber) payload.poNumber = invoice.poNumber;
        const res = await createInvoice(payload);
        setInvoice(res.data.response);
        navigate(`/project-details/${projectPid}/invoices/${res.data.response.invoicePid}`, { replace: true });
      } else {
        const payload = {
          invoicePid: invoice.invoicePid,
          documentTitle: invoice.documentTitle,
          currency: invoice.currency,
          currencySymbol: invoice.currencySymbol,
          theme: invoice.theme,
          from: invoice.from,
          to: invoice.to,
          items: invoice.items,
          taxes: invoice.taxes,
          discounts: invoice.discounts,
          customFields: invoice.customFields,
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate,
          poNumber: invoice.poNumber,
          status: targetStatus,
        };
        const res = await updateInvoice(payload);
        setInvoice(res.data.response);
      }
      toast.success("Invoice saved successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice.invoicePid) {
      toast.error("Save the invoice before downloading a PDF");
      return;
    }
    setExporting(true);
    try {
      await downloadInvoicePdf(invoice.invoicePid, `${invoice.invoiceNumber || "invoice"}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to download PDF");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => window.print();

  const handleSendEmail = async () => {
    if (!invoice.invoicePid) {
      toast.error("Save the invoice before sending it");
      return;
    }
    if (!emailTo) return;
    setSending(true);
    try {
      const res = await sendInvoice(invoice.invoicePid, emailTo);
      setInvoice((inv) => ({ ...inv, ...res.data.response }));
      toast.success("Invoice sent successfully");
      setShowEmailBox(false);
      setEmailTo("");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!invoice.invoicePid) return;
    setMarkingPaid(true);
    try {
      const res = await markInvoicePaid(invoice.invoicePid);
      setInvoice((inv) => ({ ...inv, ...res.data.response }));
      toast.success("Invoice marked as paid");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to mark invoice as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const totals = computeTotals(invoice);
  const accent = invoice.theme?.accentColor || "#2563eb";

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }} className="no-print">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 8 }}
              onClick={() => navigate(`/project-details/${projectPid}/invoices`)}>
              <ArrowLeft size={14} /> Invoices
            </button>
            <div>
              <h5 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>{invoiceId ? "Edit Invoice" : "New Invoice"}</h5>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>{invoice.invoiceNumber || "Auto-generated on save"}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handlePrint}>
              <Printer size={14} /> Print
            </button>
            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => setShowEmailBox((v) => !v)} disabled={!invoice.invoicePid} title={!invoice.invoicePid ? "Save the invoice first" : undefined}>
              <Mail size={14} /> Email
            </button>
            <button className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handleDownloadPdf} disabled={exporting || !invoice.invoicePid} title={!invoice.invoicePid ? "Save the invoice first" : undefined}>
              <Download size={14} /> {exporting ? "Exporting…" : "Download PDF"}
            </button>
            {invoice.invoicePid && invoice.status !== "paid" && (
              <button className="btn btn-outline-success btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handleMarkPaid} disabled={markingPaid}>
                <CheckCircle2 size={14} /> {markingPaid ? "Marking…" : "Mark as Paid"}
              </button>
            )}
            <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => handleSave()} disabled={saving}>
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {showEmailBox && (
          <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
            <input
              type="email"
              placeholder="client@email.com"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              style={{ ...field, maxWidth: 320 }}
            />
            <button className="btn btn-primary btn-sm" style={{ borderRadius: 8 }} onClick={handleSendEmail} disabled={sending}>{sending ? "Sending…" : "Send"}</button>
          </div>
        )}

        <div className="row g-3">
          {/* ── Editor column ── */}
          <div className="col-12 col-xl-6 no-print">
            <Card title="Branding & Currency">
              <div className="row g-3">
                <div className="col-6">
                  <label style={label}>Accent Color</label>
                  <input type="color" value={accent} onChange={(e) => updateSection("theme", { accentColor: e.target.value })} style={{ width: "100%", height: 36, border: "1px solid #e2e8f0", borderRadius: 8, padding: 2 }} />
                </div>
                <div className="col-6">
                  <label style={label}>Currency</label>
                  <select style={field} value={invoice.currency} onChange={(e) => onCurrencyChange(e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label style={label}>Logo</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {logoSrc && <img src={logoSrc} alt="logo" style={{ height: 40, borderRadius: 6, border: "1px solid #e2e8f0" }} />}
                    <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => fileInputRef.current?.click()}>
                      <ImageIcon size={13} /> Upload Logo
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onLogoUpload} />
                    {invoice.theme?.logoUrl && (
                      <button type="button" className="btn btn-outline-danger btn-sm" style={{ borderRadius: 8 }} onClick={() => updateSection("theme", { logoUrl: "" })}>Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <Card title="From (Your Business)">
              <div className="row g-2">
                <div className="col-12"><label style={label}>Name</label><input style={field} value={invoice.from.name} onChange={(e) => updateSection("from", { name: e.target.value })} /></div>
                <div className="col-12"><label style={label}>Address & Details</label><textarea rows={2} style={field} value={invoice.from.details} onChange={(e) => updateSection("from", { details: e.target.value })} /></div>
              </div>
            </Card>

            <Card title="Bill To (Client)" extra={
              <button type="button" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 7, fontSize: 11 }}
                onClick={() => navigate(`/project-details/${projectPid}/invoices/settings`)}>
                Edit in Billing Settings
              </button>
            }>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{invoice.to.name || "Not set"}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b", whiteSpace: "pre-line" }}>{invoice.to.details}</p>
              <p style={{ margin: "8px 0 0", fontSize: 11, color: "#94a3b8" }}>Client details are managed in Billing Settings, not per invoice.</p>
            </Card>

            <Card title="Invoice Details">
              <div className="row g-2">
                <div className="col-12"><label style={label}>Document Title</label><input style={field} value={invoice.documentTitle} onChange={(e) => update({ documentTitle: e.target.value })} /></div>
                <div className="col-4"><label style={label}>Invoice #</label><input style={field} value={invoice.invoiceNumber} onChange={(e) => update({ invoiceNumber: e.target.value })} /></div>
                <div className="col-4"><label style={label}>Issue Date</label><input type="date" style={field} value={invoice.issueDate} onChange={(e) => update({ issueDate: e.target.value })} /></div>
                <div className="col-4"><label style={label}>Due Date</label><input type="date" style={field} value={invoice.dueDate} onChange={(e) => update({ dueDate: e.target.value })} /></div>
                <div className="col-6"><label style={label}>PO Number</label><input style={field} value={invoice.poNumber} onChange={(e) => update({ poNumber: e.target.value })} /></div>
                <div className="col-6">
                  <label style={label}>Status</label>
                  <select style={field} value={invoice.status} onChange={(e) => update({ status: e.target.value })}>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
            </Card>

            <Card title="Line Items" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }} onClick={addItem}>
                <Plus size={13} /> Add Item
              </button>
            }>
              {invoice.items.map((it, i) => (
                <div key={i} className="row g-2 align-items-center" style={{ marginBottom: 8 }}>
                  <div className="col-6"><input style={field} placeholder="Description" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} /></div>
                  <div className="col-2"><input type="number" min="0" style={field} placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} /></div>
                  <div className="col-3"><input type="number" min="0" style={field} placeholder="Rate" value={it.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} /></div>
                  <div className="col-1">
                    <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeItem(i)} disabled={invoice.items.length === 1}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </Card>

            <Card title="Taxes" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }}
                onClick={() => addRow("taxes", { label: "", type: "percent", value: 0 })}>
                <Plus size={13} /> Add Tax
              </button>
            }>
              {invoice.taxes.map((t, i) => (
                <div key={i} className="row g-2 align-items-center" style={{ marginBottom: 8 }}>
                  <div className="col-5"><input style={field} placeholder="e.g. CGST" value={t.label} onChange={(e) => updateRow("taxes", i, { label: e.target.value })} /></div>
                  <div className="col-3"><input type="number" style={field} value={t.value} onChange={(e) => updateRow("taxes", i, { value: e.target.value })} /></div>
                  <div className="col-3">
                    <select style={field} value={t.type} onChange={(e) => updateRow("taxes", i, { type: e.target.value })}>
                      <option value="percent">%</option>
                      <option value="flat">Flat</option>
                    </select>
                  </div>
                  <div className="col-1">
                    <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeRow("taxes", i)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </Card>

            <Card title="Discounts" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }}
                onClick={() => addRow("discounts", { label: "", type: "percent", value: 0 })}>
                <Plus size={13} /> Add Discount
              </button>
            }>
              {invoice.discounts.map((d, i) => (
                <div key={i} className="row g-2 align-items-center" style={{ marginBottom: 8 }}>
                  <div className="col-5"><input style={field} placeholder="e.g. Early bird" value={d.label} onChange={(e) => updateRow("discounts", i, { label: e.target.value })} /></div>
                  <div className="col-3"><input type="number" style={field} value={d.value} onChange={(e) => updateRow("discounts", i, { value: e.target.value })} /></div>
                  <div className="col-3">
                    <select style={field} value={d.type} onChange={(e) => updateRow("discounts", i, { type: e.target.value })}>
                      <option value="percent">%</option>
                      <option value="flat">Flat</option>
                    </select>
                  </div>
                  <div className="col-1">
                    <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeRow("discounts", i)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </Card>

            {invoice.customFields.map((f, i) => (
              <Card key={i} title={f.label || "Custom Field"} extra={
                <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeRow("customFields", i)}>
                  <Trash2 size={13} />
                </button>
              }>
                <div className="row g-2">
                  <div className="col-12"><label style={label}>Label</label><input style={field} placeholder="e.g. About Us" value={f.label} onChange={(e) => updateRow("customFields", i, { label: e.target.value })} /></div>
                  <div className="col-12"><label style={label}>Value</label><textarea rows={2} style={field} value={f.value} onChange={(e) => updateRow("customFields", i, { value: e.target.value })} /></div>
                </div>
              </Card>
            ))}
            <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 mb-4" style={{ borderRadius: 7 }}
              onClick={() => addRow("customFields", { label: "", value: "" })}>
              <Plus size={13} /> Add Field
            </button>
          </div>

          {/* ── Preview column ── */}
          <div className="col-12 col-xl-6" style={{ alignSelf: "flex-start", position: "sticky", top: 85 }}>
            <div className="printable-invoice" style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05)", padding: 32, fontFamily: "Arial, sans-serif", color: "#1e293b", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 28 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {logoSrc && (
                    <img src={logoSrc} alt="logo" style={{ height: 48, marginBottom: 10 }} />
                  )}
                  <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{invoice.from.name || "Your Business"}</p>
                  <p style={{ margin: 0, fontSize: 12, whiteSpace: "pre-line", color: "#475569", overflowWrap: "anywhere" }}>{invoice.from.details}</p>
                </div>
                <div style={{ textAlign: "right", minWidth: 0, flexShrink: 0 }}>
                  <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 1, color: accent }}>{invoice.documentTitle || "INVOICE"}</h2>
                  <p style={{ margin: "4px 0 0", fontSize: 12, fontWeight: 700 }}>{invoice.invoiceNumber}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>Issued: {invoice.issueDate || "—"}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>Due: {invoice.dueDate || "—"}</p>
                  {invoice.poNumber && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>PO: {invoice.poNumber}</p>}
                  <p style={{ margin: "8px 0 0", fontSize: 14, fontWeight: 800, color: accent }}>Total: {invoice.currencySymbol}{totals.total.toFixed(2)}</p>
                </div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Bill To</p>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{invoice.to.name || "Client Name"}</p>
                <p style={{ margin: 0, fontSize: 12, whiteSpace: "pre-line", color: "#475569", overflowWrap: "anywhere" }}>{invoice.to.details}</p>
              </div>

              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", marginBottom: 18 }}>
                <thead>
                  <tr style={{ background: accent }}>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700 }}>Description</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 60 }}>Qty</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 90 }}>Rate</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 90 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((it, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 10px", fontSize: 12, overflowWrap: "anywhere" }}>{it.description || "—"}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right" }}>{it.quantity || 0}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right" }}>{invoice.currencySymbol}{(Number(it.rate) || 0).toFixed(2)}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right", fontWeight: 600 }}>{invoice.currencySymbol}{((Number(it.quantity) || 0) * (Number(it.rate) || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 22 }}>
                <div style={{ width: 240 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                    <span>Subtotal</span><span>{invoice.currencySymbol}{totals.subtotal.toFixed(2)}</span>
                  </div>
                  {totals.taxLines.map((t, i) => (
                    <div key={`tax-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                      <span>{t.label || "Tax"} {t.type === "percent" ? `(${t.value || 0}%)` : ""}</span><span>{invoice.currencySymbol}{t.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  {totals.discountLines.map((d, i) => (
                    <div key={`disc-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                      <span>{d.label || "Discount"} {d.type === "percent" ? `(${d.value || 0}%)` : ""}</span><span>-{invoice.currencySymbol}{d.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, padding: "8px 0 0", marginTop: 6, borderTop: `2px solid ${accent}`, color: accent }}>
                    <span>Total</span><span>{invoice.currencySymbol}{totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {invoice.customFields.filter((f) => f.value).map((f, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <p style={{ margin: "0 0 3px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>{f.label || "Note"}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#475569", whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{f.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default InvoiceEditor;
